import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { FCM } from '@capacitor-community/fcm';
import { NavController, ToastController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { environment } from 'src/environments/environment';
import { BYPASS_HEAD } from '../interceptor/token.interceptor';
import { AppState } from '../store/app.state';
import { getVoucherList } from '../store/actions/voucher.actions';
import { NotificationService } from './notification.service';

/**
 * Device push via FCM. Registers this install's token with dswd-api after login
 * and removes it on logout. No-op on the web build; the inbox still works there.
 */
@Injectable({
  providedIn: 'root',
})
export class PushService {

  private fcmToken: string | null = null;
  // Kept here because the store's session is already cleared when the logout effect runs.
  private authToken: string | null = null;
  private listening = false;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone,
    private navCtrl: NavController,
    private toastController: ToastController,
    private store: Store<AppState>,
    private notificationService: NotificationService,
  ) { }

  async register(authToken: string): Promise<void> {
    this.authToken = authToken;
    this.notificationService.refreshUnreadCount();

    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      let permission = await PushNotifications.checkPermissions();
      if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
        permission = await PushNotifications.requestPermissions();
      }
      if (permission.receive !== 'granted') {
        return;
      }

      await this.addListeners();
      await PushNotifications.register();
    } catch (err) {
      console.error('PushService.register', err);
    }
  }

  /** Best-effort: a failure here must never block logging out. */
  async unregister(): Promise<void> {
    const token = this.fcmToken;
    const authToken = this.authToken;
    this.fcmToken = null;
    this.authToken = null;
    this.notificationService.reset();

    if (!token || !authToken) {
      return;
    }

    try {
      await this.http.request('DELETE', environment.apiUrl + 'api/v1/mobile/devices', {
        body: { token },
        headers: this.authHeaders(authToken),
        context: new HttpContext().set(BYPASS_HEAD, true),
      }).toPromise();
    } catch (err) {
      console.error('PushService.unregister', err);
    }
  }

  private async addListeners(): Promise<void> {
    if (this.listening) {
      return;
    }
    this.listening = true;

    await PushNotifications.addListener('registration', async () => {
      // On iOS the native token is an APNs token; the backend needs the FCM one.
      const { token } = await FCM.getToken();
      await this.saveToken(token);
    });

    await PushNotifications.addListener('registrationError', (err) => {
      console.error('PushService registrationError', err);
    });

    // Foreground: the OS shows nothing, so surface it in-app.
    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      this.ngZone.run(async () => {
        this.notificationService.refreshUnreadCount();
        this.refreshFor(notification.data?.type);
        const toast = await this.toastController.create({
          header: notification.title,
          message: notification.body,
          duration: 4000,
          position: 'top',
          buttons: [{ text: 'View', handler: () => this.open(notification.data) }],
        });
        await toast.present();
      });
    });

    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      this.ngZone.run(() => {
        this.notificationService.refreshUnreadCount();
        this.refreshFor(action.notification.data?.type);
        this.open(action.notification.data);
      });
    });
  }

  private async saveToken(token: string): Promise<void> {
    if (!token || !this.authToken) {
      return;
    }

    try {
      await this.http.post(environment.apiUrl + 'api/v1/mobile/devices', {
        token,
        platform: Capacitor.getPlatform(),
      }, {
        headers: this.authHeaders(this.authToken),
        context: new HttpContext().set(BYPASS_HEAD, true),
      }).toPromise();
      this.fcmToken = token;
    } catch (err) {
      console.error('PushService.saveToken', err);
    }
  }

  private refreshFor(type: string | undefined): void {
    if (type === 'voucher_issued') {
      this.store.dispatch(getVoucherList());
    }
  }

  private open(data: any): void {
    if (!this.authToken) {
      return;
    }
    if (data?.type === 'voucher_issued') {
      this.navCtrl.navigateRoot('/home/dashboard');
      return;
    }
    this.navCtrl.navigateForward('/home/notifications');
  }

  private authHeaders(authToken: string): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json',
      Authorization: `Bearer ${authToken}`,
    });
  }
}
